import { storageManager } from "./storageManager.js";
import { cleanupOrphanedFiles, cleanupFailedFiles } from "./fileUpload.js";
import { cleanupOldFiles } from "./fileStorage.js";
import { db } from "./index.js";

/**
 * Centralized Cleanup Manager
 * Handles automatic cleanup, storage monitoring, and maintenance tasks
 */

export class CleanupManager {
  constructor() {
    this.isRunning = false;
    this.cleanupInterval = null;
    this.lastCleanup = null;
    this.cleanupIntervalMinutes = 30; // Run cleanup every 30 minutes
  }

  // Start automatic cleanup processes
  start() {
    if (this.isRunning) {
      console.log("Cleanup manager already running");
      return;
    }

    this.isRunning = true;
    console.log("Starting cleanup manager");

    // Run initial cleanup after 5 minutes
    setTimeout(() => this.performFullCleanup(), 5 * 60 * 1000);

    // Schedule periodic cleanup
    this.cleanupInterval = setInterval(
      () => this.performFullCleanup(),
      this.cleanupIntervalMinutes * 60 * 1000
    );
  }

  // Stop automatic cleanup
  stop() {
    if (!this.isRunning) return;

    this.isRunning = false;
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    console.log("Cleanup manager stopped");
  }

  // Perform comprehensive cleanup
  async performFullCleanup() {
    if (!this.isRunning) return;

    console.log("Starting full cleanup cycle");
    const startTime = Date.now();

    try {
      // Check storage status first
      const storageStatus = await storageManager.getStorageStatus();
      console.log("Storage status:", storageStatus);

      const results = {
        orphanedFiles: 0,
        oldFiles: 0,
        failedRoutes: 0,
        errors: [],
      };

      // 1. Clean up orphaned files (files without queue entries)
      try {
        const orphanedResult = await cleanupOrphanedFiles();
        results.orphanedFiles = orphanedResult.deletedCount;
        if (orphanedResult.errors.length > 0) {
          results.errors.push(...orphanedResult.errors);
        }
      } catch (error) {
        console.error("Orphaned files cleanup failed:", error);
        results.errors.push(`Orphaned cleanup: ${error.message}`);
      }

      // 2. Clean up old files based on storage pressure
      try {
        let maxAge = 7; // Default 7 days

        if (storageStatus.status === "critical") {
          maxAge = 1; // Aggressive cleanup - 1 day
        } else if (storageStatus.status === "warning") {
          maxAge = 3; // Moderate cleanup - 3 days
        }

        const oldFilesResult = await cleanupOldFiles(maxAge);
        results.oldFiles = oldFilesResult;
      } catch (error) {
        console.error("Old files cleanup failed:", error);
        results.errors.push(`Old files cleanup: ${error.message}`);
      }

      // 3. Clean up failed routes that are more than 24 hours old
      try {
        const failedRoutesResult = await this.cleanupOldFailedRoutes();
        results.failedRoutes = failedRoutesResult.cleanedCount;
        if (failedRoutesResult.errors.length > 0) {
          results.errors.push(...failedRoutesResult.errors);
        }
      } catch (error) {
        console.error("Failed routes cleanup failed:", error);
        results.errors.push(`Failed routes cleanup: ${error.message}`);
      }

      const duration = Date.now() - startTime;
      console.log(`Full cleanup completed in ${duration}ms:`, results);

      this.lastCleanup = new Date();

      return results;
    } catch (error) {
      console.error("Full cleanup failed:", error);
      return {
        error: error.message,
        orphanedFiles: 0,
        oldFiles: 0,
        failedRoutes: 0,
        errors: [error.message],
      };
    }
  }

  // Clean up failed routes that are old
  async cleanupOldFailedRoutes() {
    try {
      // Get all routes with failed files older than 24 hours
      const cutoffTime = new Date();
      cutoffTime.setHours(cutoffTime.getHours() - 24);

      const oldFailedEntries = await db.fileQueue
        .where("upload_status")
        .equals("failed")
        .and((entry) => new Date(entry.created_at) < cutoffTime)
        .toArray();

      // Group by route ID
      const routeIds = [
        ...new Set(oldFailedEntries.map((entry) => entry.sanitation_route_id)),
      ];

      let cleanedCount = 0;
      let errors = [];

      for (const routeId of routeIds) {
        try {
          const result = await cleanupFailedFiles(routeId);
          if (result.success) {
            cleanedCount += result.cleanedCount;
          } else {
            errors.push(`Route ${routeId}: ${result.error}`);
          }
        } catch (error) {
          console.error(`Error cleaning failed route ${routeId}:`, error);
          errors.push(`Route ${routeId}: ${error.message}`);
        }
      }

      return {
        success: true,
        cleanedCount,
        errors,
      };
    } catch (error) {
      return {
        success: false,
        cleanedCount: 0,
        errors: [error.message],
      };
    }
  }

  // Get cleanup statistics
  async getCleanupStats() {
    try {
      const fileBlobs = await db.fileBlobs.count();
      const fileQueue = await db.fileQueue.count();
      const pendingFiles = await db.fileQueue
        .where("upload_status")
        .equals("pending")
        .count();
      const failedFiles = await db.fileQueue
        .where("upload_status")
        .equals("failed")
        .count();

      return {
        totalFiles: fileBlobs,
        queuedFiles: fileQueue,
        pendingFiles,
        failedFiles,
        lastCleanup: this.lastCleanup,
        isRunning: this.isRunning,
      };
    } catch (error) {
      console.error("Error getting cleanup stats:", error);
      return {
        error: error.message,
        totalFiles: 0,
        queuedFiles: 0,
        pendingFiles: 0,
        failedFiles: 0,
        lastCleanup: null,
        isRunning: false,
      };
    }
  }

  // Manual cleanup trigger
  async performManualCleanup() {
    console.log("Manual cleanup triggered");
    return await this.performFullCleanup();
  }
}

// Create singleton instance
export const cleanupManager = new CleanupManager();

// Auto-start cleanup manager when module loads
if (typeof window !== "undefined") {
  // Start cleanup manager after page load
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      cleanupManager.start();
    });
  } else {
    // DOM already loaded
    cleanupManager.start();
  }
}
